import "./UhfFooter.css";

import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { getLocaleWithScript } from "@gamedev-web/gamedev-sdk-core";
import { getUhfFooter, getUhfFooterMode, UhfFooterMode } from "@gamedev-web/gamedev-uhf";

import { RawUhfContainer } from "../RawUhfContainer";

import { localePickerPath } from "../../../constants/localePicker";
import type { RootState } from "../../../redux/reducers/rootReducer";
export const UhfFooter = () => {
  const mode: UhfFooterMode = useSelector(getUhfFooterMode);
  // Provides a way to opt-out of the UHF footer, however, the caller is
  // responsible for compliance and accessibility
  if (mode === UhfFooterMode.Off) {
    return null;
  }

  return <UhfFooterRender mode={mode} />;
};

type Props = Readonly<{
  mode: UhfFooterMode;
}>;

const UhfFooterRender = ({ mode }: Props) => {
  const footerHtml = useSelector((state: RootState) => getUhfFooter(state));
  const locale = useSelector(getLocaleWithScript);
  const history = useHistory();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const localePickerLink =
      containerRef.current?.querySelector<HTMLAnchorElement>("#locale-picker-link");
    if (!localePickerLink) {
      return;
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const search = new URLSearchParams({ returnTo }).toString();
    localePickerLink.setAttribute("href", `/${locale}${localePickerPath}/?${search}`);

    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (localePickerLink.target && localePickerLink.target !== "_self")
      ) {
        return;
      }

      event.preventDefault();
      history.push({
        pathname: localePickerPath,
        search: `?${search}`,
      });
    };

    localePickerLink.addEventListener("click", handleClick);
    return () => localePickerLink.removeEventListener("click", handleClick);
  }, [footerHtml, history, locale, location.hash, location.pathname, location.search]);

  return (
    <RawUhfContainer
      htmlContent={footerHtml}
      className={`uhf-footer uhf-footer-mode-${mode.toLowerCase()}`}
      containerRef={containerRef}
    />
  );
};
